// Гашение каналов промпт-бара: что ещё можно добавить к уже набранному.
//
// Само правило исполнимости живёт в `entities/model/lib/channel-availability`
// и опирается на данные бэкенда (`channelRequirements` + `providerFeatureSets`).
// Здесь только перевод: живое состояние бара → набор заполненных каналов, и
// ответ правила → «гасить слот, показать такую подсказку». Своей таблицы
// «провайдер → каналы» тут намеренно нет — она разъехалась бы с маршрутизацией.

import { isChannelAvailable, type ChannelFill } from "~/entities/model";

import type { GenerationChannel, ModeConfig } from "~/shared/data/chat-models";

/** Состояние бара, из которого видно, какие каналы человек уже заполнил. */
export interface PromptChannelState {
    /** Сколько роль-слотов кадров занято. */
    readonly filledFrameRoles: number;
    /** Сколько элементов библиотеки выбрано. */
    readonly selectedElements: number;
    /** Сколько референсных картинок набрано. */
    readonly referenceImages: number;
    /** Сколько видео во вложениях. */
    readonly videoAttachments: number;
    /** Включён ли звук в настройках чата. */
    readonly audioEnabled: boolean;
}

export interface ChannelGate {
    readonly available: boolean;
    /** Ключ подсказки «почему недоступно»; null — канал доступен. */
    readonly hintKey: string | null;
}

const AVAILABLE: ChannelGate = { available: true, hintKey: null };

/** Канал есть у модели, но его не исполняет ни один подключённый провайдер. */
const REFERENCES_UNSUPPORTED_HINT = "prompt.references.unavailable";

/** Канал исполним сам по себе, но не вместе с уже заполненными. */
const CONFLICT_HINT_KEYS: Partial<Record<GenerationChannel, string>> = {
    frame_pair: "prompt.imageRoleSlots.unavailableWith",
    elements: "prompt.elements.unavailableWith",
    reference_images: "prompt.references.unavailableWith",
};

/**
 * Заполненные каналы по состоянию бара.
 *
 * `frame_pair` — именно пара: одиночный первый кадр не требует от исполнителя
 * ничего, требование появляется со вторым кадром (`first_last_frame`).
 */
export function buildChannelFill(state: PromptChannelState): ChannelFill {
    return {
        frame_pair: state.filledFrameRoles >= 2,
        elements: state.selectedElements > 0,
        reference_images: state.referenceImages > 0,
        input_video: state.videoAttachments > 0,
        sound: state.audioEnabled,
    };
}

/** Доступен ли канал вместе с уже заполненными — и что сказать, если нет. */
export function channelGate(
    mode: ModeConfig | null,
    fill: ChannelFill,
    channel: GenerationChannel,
): ChannelGate {
    if (!mode) return AVAILABLE;
    // У референсов две разные причины, и человеку они читаются по-разному:
    // «канал никто из подключённых не исполняет» (бэкенд отдаёт отдельным
    // флагом) и «не сочетается с тем, что уже набрано». Первая проверяется
    // раньше: она про сам канал, а не про сочетание, и подсказка должна назвать
    // именно её.
    if (channel === "reference_images" && !mode.referenceImagesSupported) {
        return { available: false, hintKey: REFERENCES_UNSUPPORTED_HINT };
    }
    if (!isChannelAvailable(mode, fill, channel)) {
        return { available: false, hintKey: CONFLICT_HINT_KEYS[channel] ?? null };
    }
    return AVAILABLE;
}

/** Подсказки колонок бара: null — колонка доступна, гасить нечего. */
export interface PromptChannelHints {
    readonly frames: string | null;
    readonly elements: string | null;
    readonly references: string | null;
}

/**
 * Что гасить в баре при текущем наборе входов — одним вызовом на три колонки.
 *
 * Отдаются ключи, а не тексты: перевод делает компонент, у которого есть `t`,
 * а правило остаётся чистым и проверяемым без React.
 */
export function promptChannelHints(mode: ModeConfig | null, state: PromptChannelState): PromptChannelHints {
    const fill = buildChannelFill(state);
    return {
        frames: channelGate(mode, fill, "frame_pair").hintKey,
        elements: channelGate(mode, fill, "elements").hintKey,
        references: channelGate(mode, fill, "reference_images").hintKey,
    };
}
